import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  Popper,
  Fade,
} from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { supabase } from '../supabaseClient';
import { useAuth } from '../AuthContext';
import { authedFetch } from '../utils/authedFetch';
import { useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL;
const ACCENT = 'rgb(219, 234, 254)';
const ACCENT_BORDER = '#60A5FA';

function buildPublicUrl(bucket, filePath) {
  const { data } = supabase.storage.from(bucket).getPublicUrl(filePath);
  return data.publicUrl;
}

export default function ImagingLibraryBrowser({
  title = 'Imaging library',
  subtitle = 'Search the image library.',
  actionLabel = 'Select image',
  actionDisabledLabel = 'Selected',
  onAction,
  isActionDisabled,
  onSelect,
  defaultIncludePublic = false,
  defaultIncludeUser = true,
  showSourceToggles = true,
  showSelectedBar = true,
}) {
  const { session } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);

  const [filters, setFilters] = useState({
    q: '',
    modality: '',
    image_type: '',
    body_part: '',
  });

  const [includePublic, setIncludePublic] = useState(defaultIncludePublic);
  const [includeUser, setIncludeUser] = useState(defaultIncludeUser);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [hoverAnchorEl, setHoverAnchorEl] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);

  const hoverOpen = Boolean(hoverAnchorEl);

  const selectedItem = useMemo(
    () => items.find((item) => item.library_key === selectedKey) || null,
    [items, selectedKey]
  );

  const fetchImages = async (overrideFilters = null) => {
    const activeFilters = overrideFilters || filters;

    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();

      if (activeFilters.q.trim()) params.set('q', activeFilters.q.trim());
      if (activeFilters.modality) params.set('modality', activeFilters.modality);
      if (activeFilters.image_type.trim()) {
        params.set('image_type', activeFilters.image_type.trim());
      }
      if (activeFilters.body_part.trim()) {
        params.set('body_part', activeFilters.body_part.trim());
      }

      params.set('includePublic', String(includePublic));
      params.set('includeUser', String(includeUser));

      const res = await authedFetch({
        accessToken: session?.access_token,
        apiBase: API_BASE,
        path: `/api/imaging-library/all?${params.toString()}`,
        navigate,
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const rows = await res.json();

      const hydrated = rows.map((row) => {
        const filePath = row.file_path || row.filepath;
        const source = row.source || 'public';

        return {
          ...row,
          file_path: filePath,
          source,
          library_key: `${source}:${row.id}`,
          image_type: row.image_type || row.study_type || '',
          study_type: row.study_type || row.image_type || '',
          imageUrl: row.public_url || buildPublicUrl(row.bucket, filePath),
        };
      });

      setItems(hydrated);
    } catch (err) {
      console.error('Failed to load imaging library:', err);
      setError('Failed to load imaging library.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!session?.access_token) return;
    fetchImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.access_token, includePublic, includeUser]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleReset = () => {
    const empty = {
      q: '',
      modality: '',
      image_type: '',
      body_part: '',
    };

    setFilters(empty);
    fetchImages(empty);
  };

  const handleSelect = (item) => {
    setSelectedKey(item.library_key);
    onSelect?.(item);
  };

  const handleAction = () => {
    if (!selectedItem) return;
    onAction?.(selectedItem);
  };

  const handleHoverOpen = (event, item) => {
    setHoverAnchorEl(event.currentTarget);
    setHoveredItem(item);
  };

  const handleHoverClose = () => {
    setHoverAnchorEl(null);
    setHoveredItem(null);
  };

  const selectedActionDisabled =
    !selectedItem || Boolean(isActionDisabled?.(selectedItem));

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${ACCENT_BORDER}`,
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: 'white',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            bgcolor: ACCENT,
            borderBottom: `1px solid ${ACCENT_BORDER}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <ImageOutlinedIcon />
          <Box>
            <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          {showSourceToggles && (
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeUser}
                    onChange={(e) => setIncludeUser(e.target.checked)}
                  />
                }
                label="My uploads"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={includePublic}
                    onChange={(e) => setIncludePublic(e.target.checked)}
                  />
                }
                label="Public library"
              />
            </Stack>
          )}

          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              <TextField
                label="Search"
                placeholder="Search title, modality, type, body part or report"
                value={filters.q}
                onChange={(e) => handleFilterChange('q', e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  ),
                }}
              />

              <TextField
                select
                label="Modality"
                value={filters.modality}
                onChange={(e) => handleFilterChange('modality', e.target.value)}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="xray">X-ray</MenuItem>
                <MenuItem value="uss">Ultrasound</MenuItem>
                <MenuItem value="ct">CT</MenuItem>
                <MenuItem value="mri">MRI</MenuItem>
                <MenuItem value="ecg">ECG</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>

              <TextField
                label="Image type"
                value={filters.image_type}
                onChange={(e) => handleFilterChange('image_type', e.target.value)}
                placeholder="e.g. CXR, 12-lead ECG"
                sx={{ minWidth: 180 }}
              />

              <TextField
                label="Body part"
                value={filters.body_part}
                onChange={(e) => handleFilterChange('body_part', e.target.value)}
                placeholder="e.g. chest, abdomen"
                sx={{ minWidth: 160 }}
              />
            </Stack>

            <Stack direction="row" spacing={1.5}>
              <Button
                variant="contained"
                onClick={() => fetchImages()}
                sx={{ textTransform: 'none' }}
              >
                Apply filters
              </Button>

              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleReset}
                sx={{ textTransform: 'none' }}
              >
                Reset
              </Button>
            </Stack>
          </Stack>

          {!includePublic && !includeUser && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Select at least one source.
            </Alert>
          )}

          {showSelectedBar && selectedItem && (
            <Box
              sx={{
                position: 'fixed',
                left: '50%',
                bottom: 24,
                transform: 'translateX(-50%)',
                zIndex: 1600,
                width: {
                  xs: 'calc(100vw - 24px)',
                  sm: 560,
                  md: 680,
                },
                maxWidth: 'calc(100vw - 24px)',
                bgcolor: 'rgba(255,255,255,0.88)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(96,165,250,0.7)',
                borderRadius: 999,
                boxShadow: '0 12px 32px rgba(15,23,42,0.18)',
                px: 2,
                py: 1.25,
              }}
            >
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ minWidth: 0 }}
              >
                <CheckCircleIcon color="primary" />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      lineHeight: 1.1,
                    }}
                  >
                    Selected image
                  </Typography>

                  <Typography
                    color="text.secondary"
                    sx={{
                      fontSize: '0.8rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {selectedItem.title}
                  </Typography>
                </Box>

                {onAction && (
                  <Button
                    variant="contained"
                    onClick={handleAction}
                    disabled={selectedActionDisabled}
                    sx={{
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      borderRadius: 999,
                      px: 2,
                    }}
                  >
                    {selectedActionDisabled ? actionDisabledLabel : actionLabel}
                  </Button>
                )}
              </Stack>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mt: 2 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : items.length === 0 ? (
              <Typography color="text.secondary">No images found.</Typography>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    sm: 'repeat(3, minmax(0, 1fr))',
                    lg: 'repeat(6, minmax(0, 1fr))',
                  },
                  gap: 2,
                }}
              >
                {items.map((item) => {
                  const isSelected = item.library_key === selectedKey;

                  return (
                    <Paper
                      key={item.library_key}
                      elevation={0}
                      onClick={() => handleSelect(item)}
                      onMouseEnter={(e) => handleHoverOpen(e, item)}
                      onMouseLeave={handleHoverClose}
                      sx={{
                        border: isSelected
                          ? '2px solid #2563eb'
                          : '1px solid #d1d5db',
                        borderRadius: 2,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        transition: '0.15s ease',
                        bgcolor: item.source === 'user' ? '#f8fafc' : 'white',
                        '&:hover': {
                          boxShadow: 2,
                          borderColor: '#60A5FA',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          height: 140,
                          bgcolor: '#f8fafc',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          position: 'relative',
                        }}
                      >
                        <Box
                          component="img"
                          src={item.imageUrl}
                          alt={item.title}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />

                        <Box
                          sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            bgcolor: 'rgba(255,255,255,0.85)',
                            borderRadius: 10,
                            px: 0.75,
                            py: 0.25,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontSize: '0.75rem',
                          }}
                        >
                          <ZoomInIcon sx={{ fontSize: 14 }} />
                          Preview
                        </Box>
                      </Box>

                      <Box sx={{ p: 1 }}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          spacing={1}
                          sx={{ mb: 0.75 }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 700,
                              lineHeight: 1.2,
                              fontSize: '0.9rem',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {item.title}
                          </Typography>

                          {isSelected && (
                            <CheckCircleIcon color="primary" fontSize="small" />
                          )}
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={0.5}
                          useFlexGap
                          flexWrap="wrap"
                        >
                          {item.modality && (
                            <Chip size="small" label={item.modality} />
                          )}

                          {item.image_type && (
                            <Chip
                              size="small"
                              label={item.image_type}
                              variant="outlined"
                            />
                          )}

                          {item.body_part && (
                            <Chip
                              size="small"
                              label={item.body_part}
                              variant="outlined"
                            />
                          )}

                          <Chip
                            size="small"
                            label={item.source === 'user' ? 'My upload' : 'Public'}
                            color={item.source === 'user' ? 'primary' : 'default'}
                            variant={item.source === 'user' ? 'filled' : 'outlined'}
                          />
                        </Stack>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      </Paper>

      <Popper
        open={hoverOpen}
        anchorEl={hoverAnchorEl}
        placement="right-start"
        transition
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [12, 0],
            },
          },
        ]}
        sx={{
          zIndex: 1400,
          pointerEvents: 'none',
        }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={120}>
            <Paper
              elevation={6}
              sx={{
                p: 1,
                borderRadius: 2,
                width: 520,
                maxWidth: '40vw',
              }}
            >
              {hoveredItem && (
                <>
                  <Box
                    component="img"
                    src={hoveredItem.imageUrl}
                    alt={hoveredItem.title}
                    sx={{
                      width: '100%',
                      maxHeight: 420,
                      objectFit: 'contain',
                      display: 'block',
                      borderRadius: 1,
                      bgcolor: '#f8fafc',
                    }}
                  />

                  <Typography sx={{ mt: 1, fontWeight: 700 }}>
                    {hoveredItem.title}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={0.75}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ mt: 1 }}
                  >
                    {hoveredItem.modality && (
                      <Chip size="small" label={hoveredItem.modality} />
                    )}

                    {hoveredItem.image_type && (
                      <Chip
                        size="small"
                        label={hoveredItem.image_type}
                        variant="outlined"
                      />
                    )}

                    {hoveredItem.body_part && (
                      <Chip
                        size="small"
                        label={hoveredItem.body_part}
                        variant="outlined"
                      />
                    )}

                    <Chip
                      size="small"
                      label={hoveredItem.source === 'user' ? 'My upload' : 'Public'}
                      color={hoveredItem.source === 'user' ? 'primary' : 'default'}
                      variant={hoveredItem.source === 'user' ? 'filled' : 'outlined'}
                    />
                  </Stack>

                  {hoveredItem.description && (
                    <Typography sx={{ mt: 1, fontWeight: 250 }}>
                      {hoveredItem.description}
                    </Typography>
                  )}

                  {hoveredItem.report && (
                    <Typography sx={{ mt: 1, fontWeight: 250 }}>
                      {hoveredItem.report}
                    </Typography>
                  )}
                </>
              )}
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
}